import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';

import { DayOfRaceDate } from '@cds/betting-offer/domain-specific/horse-racing';
import { RacingConfig } from '@frontend/sports/common/client-config-data-access';
import { translationToStr } from '@frontend/sports/common/core/utils/translations';
import { RaceDetailsModel, RacingUrlParam, WinGameFixture } from '@frontend/sports/racing/feature/models';
import { IntlService } from '@frontend/vanilla/core';
import { EMPTY, Observable, catchError, map, of, switchMap } from 'rxjs';

import { getDayOfRace } from '../../../../horse-racing-shared/helpers';
import { GreyhoundRacingApiService } from '../../../../horse-racing-shared/services/greyhound-racing-api.service';
import { AbstractRacingService } from '../../../../horse-racing-shared/services/racing-api.service';

export class RaceDetailsResolver {
    constructor(
        protected apiService: AbstractRacingService,
        protected intlService: IntlService,
        protected racingConfig: RacingConfig,
    ) {}

    resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<RaceDetailsModel> {
        const isMeetingAllFixtures = route.data.isMeetingAllFixtures;

        if (isMeetingAllFixtures) {
            const meetingId = +route.params.meetingId;
            const dayOfRace = route.params.filter === RacingUrlParam.Today ? DayOfRaceDate.Today : DayOfRaceDate.Tomorrow;

            return this.buildMeetingAllFixtures(meetingId, dayOfRace);
        }

        const fixtureId = route.params.raceId;
        if (!fixtureId) {
            return EMPTY;
        }

        return this.buildFixtures(fixtureId);
    }

    private buildFixtures(fixtureId: any): Observable<RaceDetailsModel> {
        return this.apiService.getFixture(fixtureId).pipe(
            switchMap((fixture) => {
                if (!fixture) {
                    return EMPTY;
                }

                return this.buildMeetingFixtures(fixture);
            }),
            catchError(() => EMPTY),
        );
    }

    private buildMeetingFixtures(fixture: WinGameFixture) {
        const day = getDayOfRace(fixture.startDate);

        if (day === DayOfRaceDate.Tomorrow || this.intlService.formatDate(fixture.startDate, 'HH:mm') <= this.racingConfig.bogTodayThresholdTime) {
            fixture.addons.bestOddsGuarantee = false;
        }

        const def: any = {
            fixture,
            fixtures: [],
            meeting: {
                id: fixture.meeting.id,
                name: translationToStr(fixture.meeting.name),
            },
            day,
        };

        return this.apiService.getMeeting(fixture.meeting.id, day).pipe(
            map(
                (fixtures) =>
                    ({
                        fixture,
                        fixtures,
                        meeting: {
                            id: fixture.meeting.id,
                            name: translationToStr(fixture.meeting.name),
                        },
                        day,
                    }) as RaceDetailsModel,
            ),
            catchError(() => of(def)),
        );
    }

    private buildMeetingAllFixtures(meetingId: number, dayOfRace: DayOfRaceDate): Observable<RaceDetailsModel> {
        return this.apiService.getMeetingFixtures(meetingId, dayOfRace).pipe(
            map(
                (fixtures) =>
                    ({
                        allFixtures: fixtures,
                        meeting: {
                            id: fixtures[0].meeting.id,
                            name: translationToStr(fixtures[0].meeting.name),
                        },
                        day: dayOfRace,
                    }) as RaceDetailsModel,
            ),
        );
    }
}

@Injectable()
export class GreyhoundsRaceDetailsResolver extends RaceDetailsResolver {
    constructor(
        apiService: GreyhoundRacingApiService,
        protected override intlService: IntlService,
        protected override racingConfig: RacingConfig,
    ) {
        super(apiService, intlService, racingConfig);
    }
}
